/**
 * State for the canvas agent chat — the single canvas chat surface.
 *
 * Owns three things:
 *  - **Session navigation**: an in-memory stack (push/replace/back). `currentSessionId` is the
 *    top of the stack (or null) — it means "an explicit session is active"; non-UI consumers
 *    (onboarding `isAgentChatOpen`, loading-nodes, deck/image steps) rely on that meaning.
 *  - **Surface state**: `open` (the chat window is up) vs closed (just the FAB launcher), plus a
 *    persisted `sizeClass` (`windowed` floating panel vs `full-height` docked). `openSession` /
 *    `expand` open the window; `collapse()` minimizes back to the FAB.
 *  - **Composer model**: the active canvas session for the landing (`activeSessionId`),
 *    create-on-send (`send`, canvas-class), and the landing-dismiss flag
 *    (`hasStarted` ← a send from this mount OR any existing canvas chat session).
 *
 * Under `CanvasProvider` this remounts when `canvasId` changes, so all state resets per canvas.
 */

import { useCanvasFlowSessions } from "@/api/canvas";
import { useCreateFlowSession } from "@/api/flow_sessions";
import { usePostFlowMessage } from "@/api/messages";
import {
  useFloatingChatSessionStack,
  type FloatingChatSessionHistory,
  type FloatingChatSizeClass,
} from "@/components/FloatingChat/useFloatingChatSessionStack";
import { useCanvasHarnessDraft } from "@/hooks/useHarnessDraft";
import Sentry from "@/integrations/Sentry";
import {
  FLOW_SESSION_MODES,
  type FlowSession,
  type PostMessageParams,
  type ReferencedEntity,
} from "@cohesive/api-client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import { useCanvasReasoningEffort } from "../hooks/useCanvasReasoningEffort";
import { useCanvasRoute } from "./CanvasRouteContext";

export type CanvasFloatingSessionHistory = FloatingChatSessionHistory;

/** Window size class: a small floating panel vs a full-height docked panel. */
export type CanvasChatSizeClass = FloatingChatSizeClass;

/** Result of a composer send (mirrors the shared MessageInput send contract). */
export interface CanvasChatSendResult {
  messageSent: boolean;
}

export interface CanvasFloatingChatContextValue {
  /** Top-of-stack expanded session id, or null. Means "an explicit session is active". */
  currentSessionId: string | null;
  canGoBack: boolean;
  /** Second arg defaults to `"push"` — pass `"replace"` only when swapping the top session. Also expands the window. */
  openSession: (
    sessionId: string,
    history?: CanvasFloatingSessionHistory,
  ) => void;
  goBack: () => void;
  /** Clears the stack and closes the window back to the FAB (does NOT unmount the surface). */
  closeChat: () => void;

  /** True when the chat window is up; false shows only the FAB launcher. */
  open: boolean;
  /** Open the window onto a session (defaults to the active session, minting one if none). */
  expand: (sessionId?: string) => void;
  /** Minimize the window back to the FAB, keeping the session on the stack. */
  collapse: () => void;
  /** windowed (floating panel) vs full-height (docked). Persisted per canvas. */
  sizeClass: CanvasChatSizeClass;
  /** Flip between the windowed and full-height size classes. */
  toggleSizeClass: () => void;
  /** Put the window in one size class, the way the header's toggle does. */
  setSizeClass: (next: CanvasChatSizeClass) => void;

  /** The canvas session the always-present bar / landing acts on (mirrors the open session, else most-recent). */
  activeSessionId: string | null;
  /** True once the canvas has any user chat session (or one was just sent); dismisses the landing. */
  hasStarted: boolean;
  isSubmitting: boolean;
  /** Posts to the active canvas session, creating a canvas-class session on the first send. */
  send: (
    content: string,
    referencedEntities: ReferencedEntity[],
    options?: Pick<
      PostMessageParams,
      | "reasoning_effort"
      | "model"
      | "harness_reasoning_effort"
      | "permission_mode"
    >,
  ) => Promise<CanvasChatSendResult>;

  /**
   * Pixels to subtract from the bottom toolbar row `maxWidth` so it stays left of
   * the chat (shell width + `CANVAS_FLOATING_CHAT_TOOLBAR_RIGHT_MARGIN`).
   */
  bottomToolbarRightClearancePx: number;
  /** Called from `CanvasFloatingChatPanel` via ResizeObserver. */
  setBottomToolbarRightClearancePx: Dispatch<SetStateAction<number>>;
  /**
   * The chat's painted footprint from the React Flow wrapper's right edge:
   * the distance to the open window's or the FAB's painted left edge, 0
   * when neither stands on the right. The redrawn toolbar keeps its own
   * air off this (D55); `bottomToolbarRightClearancePx` is unchanged.
   */
  bottomToolbarChatFootprintPx: number;
  setBottomToolbarChatFootprintPx: Dispatch<SetStateAction<number>>;
  /**
   * Horizontal offset for the top-right share + controls row when the chat shell
   * overlaps that band (full-height dock, or a windowed panel resized upward).
   */
  topControlsRightClearancePx: number;
  /** Called from `CanvasFloatingChatPanel` via ResizeObserver. */
  setTopControlsRightClearancePx: Dispatch<SetStateAction<number>>;
  /**
   * A width drag on the chat shell is in flight. The clearances above land a
   * frame at a time while it runs, so chrome that eases into a new offset has
   * to stop easing and follow the edge — the same rule ResizablePanel applies
   * to its own transition while dragging.
   */
  isChatResizing: boolean;
  setIsChatResizing: Dispatch<SetStateAction<boolean>>;
}

const CanvasFloatingChatContext =
  createContext<CanvasFloatingChatContextValue | null>(null);

/**
 * Horizontal space reserved beside the floating chat (`right-2`) plus a small gap
 * so the bottom canvas toolbar stays clear of the popover.
 */
export const CANVAS_FLOATING_CHAT_TOOLBAR_RIGHT_MARGIN = 16;

/**
 * Distance from the React Flow wrapper top to the bottom of the share + controls
 * row (`top-4` inset + horizontal toolbar chrome). Used to detect when a
 * windowed chat panel resized upward would overlap those controls.
 */
export const CANVAS_TOP_CONTROLS_BAND_BOTTOM_PX = 56;

/** Mirrors the session visible in the open floating chat panel (not the FAB-only stack). */
export const CANVAS_FLOATING_CHAT_ACTIVE_SESSION_EVENT =
  "canvas:floating-chat-active-session";

const floatingChatActiveSessionByCanvasId = new Map<string, string | null>();

/** Last emitted visible session for a canvas — for subscribers outside the provider tree. */
export function readCanvasFloatingChatActiveSession(
  canvasId: string,
): string | null {
  return floatingChatActiveSessionByCanvasId.get(canvasId) ?? null;
}

function emitCanvasFloatingChatActiveSession(detail: {
  canvasId: string;
  flowSessionId: string | null;
}) {
  floatingChatActiveSessionByCanvasId.set(
    detail.canvasId,
    detail.flowSessionId,
  );
  window.dispatchEvent(
    new CustomEvent(CANVAS_FLOATING_CHAT_ACTIVE_SESSION_EVENT, { detail }),
  );
}

/** For callers outside React (e.g. explorer pane) — same event as legacy `canvas:open-session-pane`. */
export function emitCanvasOpenFloatingSession(detail: {
  canvasId: string;
  flowSessionId: string;
  /** Defaults to `"push"` when omitted. */
  history?: CanvasFloatingSessionHistory;
}) {
  window.dispatchEvent(
    new CustomEvent("canvas:open-session-pane", {
      detail: {
        canvasId: detail.canvasId,
        flowSessionId: detail.flowSessionId,
        history: detail.history ?? "push",
      },
    }),
  );
}

export function useCanvasFloatingChat(): CanvasFloatingChatContextValue {
  const ctx = useContext(CanvasFloatingChatContext);
  if (!ctx) {
    throw new Error(
      "useCanvasFloatingChat must be used within CanvasFloatingChatProvider",
    );
  }
  return ctx;
}

/** Canvas chat sessions the user authored directly (exclude sub-agent/action-item runs), newest first. */
function selectUserSessions(
  sessions: FlowSession[] | undefined,
): FlowSession[] {
  if (!sessions) return [];
  return [...sessions]
    .filter((s) => !s.action_item_id)
    .sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
}

export function CanvasFloatingChatProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { canvasId } = useCanvasRoute();
  const { data: rawSessions } = useCanvasFlowSessions(canvasId);
  const createFlowSession = useCreateFlowSession();
  const postMessage = usePostFlowMessage();
  const [reasoningEffort] = useCanvasReasoningEffort(canvasId ?? "");
  const [, , harnessCreateFields, harnessReady] =
    useCanvasHarnessDraft(canvasId);

  const [bottomToolbarRightClearancePx, setBottomToolbarRightClearancePx] =
    useState(0);
  const [topControlsRightClearancePx, setTopControlsRightClearancePx] =
    useState(0);
  const [bottomToolbarChatFootprintPx, setBottomToolbarChatFootprintPx] =
    useState(0);
  const [isChatResizing, setIsChatResizing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => setStarted(false), [canvasId]);

  // Shared stack/open/size-class machine. The size class persists per canvas
  // (this provider remounts per canvas, so the storage key tracks `canvasId`).
  const {
    currentSessionId,
    canGoBack,
    open,
    sizeClass,
    openSession,
    goBack,
    closeChat,
    collapse,
    toggleSizeClass,
    setSizeClass,
  } = useFloatingChatSessionStack({
    sizeClassStorageKey: `canvas_floating_chat_size_class_${canvasId ?? "__none__"}`,
    defaultSizeClass: "windowed",
  });

  const sessions = useMemo(
    () => selectUserSessions(rawSessions),
    [rawSessions],
  );

  // The session the always-present bar / landing acts on: mirror the open session so
  // collapsing keeps context, else fall back to the most-recent canvas session.
  const activeSessionId = useMemo<string | null>(() => {
    if (currentSessionId) return currentSessionId;
    return sessions[0]?.id ?? null;
  }, [currentSessionId, sessions]);

  // The presence of a session is the durable signal; `started` covers the
  // gap between the first send and the sessions query catching up, and an
  // open session covers the same gap for a session another surface minted,
  // such as a home dispatch, so the landing cannot cover its running turn.
  const hasStarted = started || sessions.length > 0 || currentSessionId != null;

  // One create at a time: expanding and the landing's send can both find no session, and either
  // repeated before the first lands would mint a second chat.
  const creating = useRef<Promise<string> | null>(null);
  const createCanvasSession = useCallback((): Promise<string> => {
    creating.current ??= createFlowSession
      .mutateAsync({
        mode: FLOW_SESSION_MODES.CANVAS_CHAT,
        class: "canvas",
        canvas_id: canvasId,
        // Stamped here whether the chat was started by the landing composer or by opening the
        // panel with nothing to open — both are the same user choosing to talk to this canvas.
        ...harnessCreateFields,
      })
      .then((session) => session.id)
      .finally(() => {
        creating.current = null;
      });
    return creating.current;
  }, [createFlowSession, canvasId, harnessCreateFields]);

  // Opening the panel with nothing to open mints a chat, which waits for this load's catalog;
  // an expand asked for meanwhile runs once it lands.
  const [expandWaiting, setExpandWaiting] = useState(false);

  const expand = useCallback(
    (sessionId?: string) => {
      // Guard against event handlers that pass a click event as the arg
      // (e.g. `onClick={expand}`): only a real string id selects a session.
      const requested = typeof sessionId === "string" ? sessionId : undefined;
      const target = requested ?? currentSessionId ?? activeSessionId;
      if (target) {
        openSession(target);
        return;
      }
      // No session yet — mint a canvas-agent session, then expand onto it.
      if (!canvasId) return;
      if (!harnessReady) {
        setExpandWaiting(true);
        return;
      }
      void (async () => {
        try {
          const id = await createCanvasSession();
          openSession(id);
        } catch (err) {
          Sentry.captureException(err);
          toast.error("Failed to open chat");
        }
      })();
    },
    [
      currentSessionId,
      activeSessionId,
      openSession,
      canvasId,
      harnessReady,
      createCanvasSession,
    ],
  );

  useEffect(() => {
    if (!expandWaiting || !harnessReady) return;
    setExpandWaiting(false);
    expand();
  }, [expandWaiting, harnessReady, expand]);

  const send = useCallback(
    async (
      content: string,
      referencedEntities: ReferencedEntity[],
      options?: Pick<
        PostMessageParams,
        | "reasoning_effort"
        | "model"
        | "harness_reasoning_effort"
        | "permission_mode"
      >,
    ): Promise<CanvasChatSendResult> => {
      const text = content.trim();
      if (!canvasId || !text) return { messageSent: false };

      setIsSubmitting(true);
      try {
        let sessionId = activeSessionId;
        if (!sessionId) {
          sessionId = await createCanvasSession();
        }
        await postMessage.mutateAsync({
          flowSessionId: sessionId,
          id: uuidv4(),
          content: text,
          referenced_entities: referencedEntities,
          reasoning_effort: options?.reasoning_effort ?? reasoningEffort,
          // Set only where the composer was driving a session that already existed — a session
          // minted just above carries its model on the create instead.
          model: options?.model,
          harness_reasoning_effort: options?.harness_reasoning_effort,
          permission_mode: options?.permission_mode,
        });
        setStarted(true);
        // Surface the window so the just-sent message + reply are visible
        // (the landing's composer is the only remaining caller of `send`).
        openSession(sessionId);
        return { messageSent: true };
      } catch (error) {
        console.error("[CanvasFloatingChat] Failed to send:", error);
        return { messageSent: false };
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      canvasId,
      activeSessionId,
      createCanvasSession,
      postMessage,
      reasoningEffort,
      openSession,
    ],
  );

  // Chats rail lives outside this provider; broadcast the session actually shown
  // in the open panel (clear when minimized to FAB or fully closed).
  useEffect(() => {
    if (!canvasId) return;
    emitCanvasFloatingChatActiveSession({
      canvasId,
      flowSessionId: open && currentSessionId ? currentSessionId : null,
    });
  }, [canvasId, open, currentSessionId]);

  const value = useMemo(
    () => ({
      currentSessionId,
      canGoBack,
      openSession,
      goBack,
      closeChat,
      open,
      expand,
      collapse,
      sizeClass,
      toggleSizeClass,
      setSizeClass,
      activeSessionId,
      hasStarted,
      isSubmitting,
      send,
      bottomToolbarRightClearancePx,
      setBottomToolbarRightClearancePx,
      bottomToolbarChatFootprintPx,
      setBottomToolbarChatFootprintPx,
      topControlsRightClearancePx,
      setTopControlsRightClearancePx,
      isChatResizing,
      setIsChatResizing,
    }),
    [
      currentSessionId,
      canGoBack,
      openSession,
      goBack,
      closeChat,
      open,
      expand,
      collapse,
      sizeClass,
      toggleSizeClass,
      setSizeClass,
      activeSessionId,
      hasStarted,
      isSubmitting,
      send,
      bottomToolbarRightClearancePx,
      bottomToolbarChatFootprintPx,
      topControlsRightClearancePx,
      isChatResizing,
    ],
  );

  return (
    <CanvasFloatingChatContext.Provider value={value}>
      {children}
    </CanvasFloatingChatContext.Provider>
  );
}

const PUBLIC_VIEW_CHAT_VALUE: CanvasFloatingChatContextValue = {
  currentSessionId: null,
  canGoBack: false,
  openSession: () => {},
  goBack: () => {},
  closeChat: () => {},
  open: false,
  expand: () => {},
  collapse: () => {},
  sizeClass: "windowed",
  toggleSizeClass: () => {},
  setSizeClass: () => {},
  activeSessionId: null,
  hasStarted: false,
  isSubmitting: false,
  send: async () => ({ messageSent: false }),
  bottomToolbarRightClearancePx: 0,
  setBottomToolbarRightClearancePx: () => {},
  bottomToolbarChatFootprintPx: 0,
  setBottomToolbarChatFootprintPx: () => {},
  topControlsRightClearancePx: 0,
  setTopControlsRightClearancePx: () => {},
  isChatResizing: false,
  setIsChatResizing: () => {},
};

/**
 * No-op floating chat for the public canvas-view SPA and the capture page.
 * EntityNode still calls `useCanvasFloatingChat`; chat UI is never mounted on
 * either surface.
 */
export function CanvasFloatingChatStubProvider({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <CanvasFloatingChatContext.Provider value={PUBLIC_VIEW_CHAT_VALUE}>
      {children}
    </CanvasFloatingChatContext.Provider>
  );
}
